import type { IEventProperty } from "@microsoft/1ds-analytics-js";
import type { StartupAnalyticsInfo } from "owa-config-types";
import { ValueKind } from "@microsoft/1ds-analytics-js";
import { hashString } from "owa-analytics-shared/lib/utils/hashString";
import { getApp } from "owa-config/lib/bootstrapOptions";
import { getPlt } from "owa-plt-data";
import { createEvent, logOneDSDatapoint } from "../OneDsWrapper";
import getConditionalAccessString from "../utils/getConditionalAccessString";
import type { AnalyticsSessionProperties, AnalyticsSettingsAddon, CoreAnalyticsOptions, } from "owa-analytics-types";
import isDevOrAutomationEnvironment from "../utils/isDevOrAutomationEnvironment";
import { getSessionElapseTime } from "owa-analytics-utils";
import { getBrowserHeight, getBrowserWidth } from "owa-config";
import { getAriaTenantToken } from "owa-analytics-shared/lib/settings/getAriaTenantToken";
import { getAnalyticsAddon } from "owa-analytics-addons";
import { getEncounteredErrorCodes } from "owa-error-codes";
import { trace } from "owa-trace";
import { serializeSessionSettings } from "./serializeSessionSettings";
import optionallyTraceDatapoint from "../utils/optionallyTraceDatapoint";
import { AriaCoreDatapoint } from "../datapoints/AriaCoreDatapoint";
import type { AccountKey, MailboxInfo } from "owa-client-types";
import { getAccountKeyForMailboxInfo } from "owa-client-types";

/**
 * Most analytics options can be re-used accross the different accounts in multi-account scenarios, so we cache it.
 * The "startupInfo" for secondary accounts most come from that account, not this object.
 */
let cachedAnalyticsOptions: CoreAnalyticsOptions | null = null;
let cachedGlobalEcsFeatures: Set<string> | null = null;

export async function logSessionDatapoint(analyticsOptions: CoreAnalyticsOptions): Promise<void> {
    // if no aria tokens are passed in, there is no point
    if (!analyticsOptions.isModuleSwitch // Do not resend the session data on a module switch
    ) {
        const addon = await getAnalyticsAddon<AnalyticsSettingsAddon>("AnalyticsSettings").executeNow();
        const globalAccountSessionProperties = await addon?.getSessionProperties(analyticsOptions.startupInfo, undefined, true /* isGlobalAccount */);
        const accountKey = addon?.getGlobalSettingsAccountKey();
        const token = getAriaTenantToken(getApp());
        
        logSessionDatapointInternal(analyticsOptions, token, globalAccountSessionProperties, accountKey, true /* isGlobalAccount */);
        
        if (!cachedAnalyticsOptions) {
            // Cache the analytics options for future use in secondary account logins. We only cache it for the first session login to ensure we have the correct startupInfo and tenant token.
            cachedAnalyticsOptions = analyticsOptions;
        }
    }
}

export async function logSecondaryAccountSessionDatapoint(accountStartupInfo: StartupAnalyticsInfo, mailboxInfo: MailboxInfo): Promise<void> {
    const addon = await getAnalyticsAddon<AnalyticsSettingsAddon>("AnalyticsSettings").executeNow();
    const secondaryAccountSessionProperties = await addon?.getSessionProperties(accountStartupInfo, mailboxInfo, false /* isGlobalAccount */);
    const token = getAriaTenantToken(getApp());
    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    
    if (!cachedAnalyticsOptions) {
        trace.warn("Analytics options are not cached yet when logging secondary account session datapoint. This should not happen as the primary account session datapoint should have been logged before any secondary account logins.", "analytics");
        return;
    }
    
    logSessionDatapointInternal(cachedAnalyticsOptions, token, secondaryAccountSessionProperties, accountKey, false /* isGlobalAccount */);
}

async function logSessionDatapointInternal(analyticsOptions: CoreAnalyticsOptions, token: string, logSessionProperties: AnalyticsSessionProperties, accountKey: AccountKey | undefined, isGlobalAccount: boolean) {
    const { userConfiguration, accountScopeUserSettings, sessionInfo, report, uberCheck, userTypeCheck, appSettingsReliabilityCheck, sessionAccountInfo, sessionImportData, webServerForest, clique, networkIno, bootFlights, hardware, isOcdiHostName, accountFeatureFlags, showPreviewLinesInListViewOcsSetting, } = logSessionProperties;
    
    const navigator = self.navigator;
    // we need to create a seperate datapoint for each event, because there is a bug
    // in the aria code which will force the events to be sent to the same tenant if
    const properties: {
        [key: string]: string | number | boolean;
    } = {
        Features: getSerializedFeatures(analyticsOptions.defaultFeatureFlags, accountFeatureFlags, isGlobalAccount),
        App: getApp(),
    };
    
    if (sessionInfo.bootType && isGlobalAccount) {
        properties.BootType = sessionInfo.bootType;
    }
    
    const serviceVersion = sessionInfo.serviceVersion;
    if (serviceVersion) {
        properties.ServiceVersion = serviceVersion;
    }
    
    const dag = sessionInfo.dag;
    if (dag) {
        properties.dag = dag;
    }
    
    const sessionElapseTime = getSessionElapseTime();
    if (sessionElapseTime) {
        properties.SessionElapseTime = sessionElapseTime;
    }
    
    // Properties that are only added to the global account session datapoint
    if (isGlobalAccount) {
        const capabilities: {
            [key: string]: boolean;
        } = {
            webdriver: navigator.webdriver,
            pdfViewerEnabled: navigator.pdfViewerEnabled,
        };
        
        if (CSS.supports("container", "inline-size")) {
            capabilities.containerInlineSizeSupported = true;
        }
        properties.Capabilities = JSON.stringify(capabilities);
        
        const errorCodes = getEncounteredErrorCodes();
        properties.ErrorCodes = JSON.stringify(errorCodes);
        
        properties.Hardware = JSON.stringify(hardware);
        
        properties.UserAgent = navigator.userAgent;
    }
    
    const serializedEcsFeatures = getSerializedEcsFeatures(report?.Settings?.Features, isGlobalAccount);
    if (serializedEcsFeatures) {
        properties.ECSFeatures = serializedEcsFeatures;
    }
    
    let hashedEmail: string | undefined;
    if (userConfiguration) {
        const userOptions = userConfiguration.UserOptions || {};
        const viewConfig = userConfiguration.ViewStateConfiguration || {};
        const Dimensions: any = {
            WindowWidth: getBrowserWidth(),
            WindowHeight: getBrowserHeight(),
        };
        if (self.screen) {
            Dimensions.ScreenWidth = self.screen.availWidth;
            Dimensions.ScreenHeight = self.screen.availHeight;
        }
        
        // Report the import data when present so we know when the user migrated from another Outlook application
        const importData = sessionImportData
            ? {
                date: sessionImportData.lastImportDate?.toISOString() ?? "",
                app: sessionImportData.lastImportApp ?? "",
                correlationId: sessionImportData.lastImportCorrelationId ?? "",
            }
            : undefined;
        
        // report the account information when present so we know what accounts the user has configured
        const accountInfo = sessionAccountInfo
            ? {
                // Report account data when present
                accounts: sessionAccountInfo.totalAccounts,
                withPuid: sessionAccountInfo.withPuid,
                office365: sessionAccountInfo.office365,
                outlookDotCom: sessionAccountInfo.outlookDotCom,
                google: sessionAccountInfo.google,
                yahoo: sessionAccountInfo.yahoo,
                icloud: sessionAccountInfo.icloud,
                imap: sessionAccountInfo.imap,
                pop3: sessionAccountInfo.pop3,
                pst: sessionAccountInfo.pst,
                other: sessionAccountInfo.other,
                sub: sessionAccountInfo.sub,
                subMsa: sessionAccountInfo.subMsa,
                subAad: sessionAccountInfo.subAad,
            }
            : undefined;
        
        let isDocumentIsolationEnabled = false;
        try {
            isDocumentIsolationEnabled = self?.isSecureContext && self?.crossOriginIsolated;
        }
        catch {
            // no-op for browsers that don't support these APIs
            trace.warn("This browser doesn't support cross-origin isolation (Document Isolation).");
        }
        
        if (bootFlights) {
            properties.BootFlights = bootFlights;
        }
        
        properties.Settings = serializeSessionSettings({
            listViewType: userOptions.GlobalListViewTypeReact,
            consumerAdsExperiment: userOptions.ConsumerAdsExperimentMode,
            globalFolderViewState: viewConfig.GlobalFolderViewState,
            beforeFolderViewBits: {
                ReadingPanePosition: userOptions.GlobalReadingPanePositionReact,
                ConditionalAccess: getConditionalAccessString(userConfiguration),
                FocusedEnabled: accountScopeUserSettings?.UserOptions?.IsFocusedInboxEnabled ??
                    true /* IsFocusedInboxEnabled is undefined for the user's first session and we treat it as true */,
                FocusedInboxBits: viewConfig.FocusedInboxBitFlags,
                ListViewBits: accountScopeUserSettings?.ViewStateConfiguration?.ListViewBitFlags ??
                    0 /* Date headers disabled */,
            },
            afterFolderViewBits: {
                PreviewText: userOptions.ShowPreviewTextInListView,
                PreviewTextLineCount: showPreviewLinesInListViewOcsSetting,
                SenderOnTop: userOptions.ShowSenderOnTopInListView,
                InlinePreviews: userOptions.ShowInlinePreviews,
                HoverActions: viewConfig.MailTriageOnHoverActions,
                Ponts: userOptions.NewEnabledPonts,
                Theme: (userOptions.ThemeStorageId || "") + "|" + userOptions.IsDarkModeTheme,
                MasterCategories: accountScopeUserSettings?.MasterCategoryList?.MasterList,
                IsEdu: !!sessionInfo?.isEdu,
                NetworkInfo: networkIno,
                Dimensions,
                AdMarket: accountScopeUserSettings?.AdMarket,
                DisplayDensity: userOptions.DisplayDensityMode,
                Domain: userConfiguration.SessionSettings?.TenantDomain,
                CerberusFlagMatch: uberCheck,
                FlagsUserMatch: userTypeCheck,
                EcsCheck: appSettingsReliabilityCheck,
                KeyboardShortcutsMode: userOptions.KeyboardShortcutsMode,
                WebServerForest: webServerForest,
                Clique: clique,
                BootFlights: bootFlights,
                XOwaForest: sessionInfo.forest,
                importData,
                accountInfo,
                DefaultFolderNames: userConfiguration.SessionSettings?.DefaultFolderNames,
                DefaultFolderIds: userConfiguration.SessionSettings?.DefaultFolderIds,
                ShowReadingPaneOnFirstLoad: userOptions.ShowReadingPaneOnFirstLoad,
                EmptyDeletedItemsOnLogoff: userOptions.EmptyDeletedItemsOnLogoff,
                IsOcdiHostName: isOcdiHostName,
                IsDocumentIsolationEnabled: isDocumentIsolationEnabled,
                ExternalDirectoryUserGuid: userConfiguration.SessionSettings?.ExternalDirectoryUserGuid,
                HasConnectedAccounts: userConfiguration.SessionSettings?.HasConnectedAccounts,
            },
        });
        properties.Dates = JSON.stringify({
            OwaLogon: userOptions.FirstOWALogon,
            MailLogon: userOptions.FirstOWAReactMailLogon,
            PeopleLogon: userOptions.FirstOWAReactPeopleLogon,
            CalendarLogon: userOptions.FirstOWAReactCalendarLogon,
            MiniLogon: userOptions.FirstOWAReactMiniLogon,
            MailboxCreate: userConfiguration.MailboxCreateDate,
            FirstMonarchLogon: userOptions.FirstMonarchLogon,
        });
        const plt = getPlt();
        if (plt && isGlobalAccount) {
            properties.LoadTime = plt;
        }
        
        const email = accountScopeUserSettings?.SessionSettings?.UserEmailAddress;
        if (email) {
            hashedEmail = hashString(email);
        }
    }
    
    const table = isGlobalAccount ? "session" : "session_secondary_accounts";
    
    optionallyTraceDatapoint(new AriaCoreDatapoint(table, undefined, properties), table, accountKey ?? "globalAccount", analyticsOptions);
    
    if (!isDevOrAutomationEnvironment()) {
        const e = createEvent(table);
        e.baseType = "session";
        e.data = properties;
        if (hashedEmail) {
            e.data.HashEmail = <IEventProperty>{
                value: hashedEmail,
                kind: ValueKind.Pii_Identity,
            };
        }
        
        // TODO: Get account key for the session datapoint
        logOneDSDatapoint({
            tenantToken: token,
            item: e,
            accountKey,
            analyticsOptions,
        });
    }
}

/**
 * Returns elements in `items` that are not present in `baseline`.
 *
 * Exported so the EcsConfigStateStamping emission sites can mirror
 * the same delta-encoding pattern session uses for secondary accounts.
 */
export function getArrayDelta(items: string[], baseline: Set<string> | string[]): string[] {
    const baselineSet = baseline instanceof Set ? baseline : new Set(baseline);
    return items.filter(item => !baselineSet.has(item));
}

/**
 * Builds the session `Features` payload string in the exact shape
 * `logSessionDatapointInternal` writes today. Exported so the
 * EcsConfigStateStamping emission sites in
 * `owa-shared-post-boot/setupAnalytics.ts`,
 * `owa-account-source-list/initializeAnalyticsForSecondaryAccount.ts`,
 * and `owa-calendar-quickcalendar-analytics-initialization/initializeQuickCalendarAnalytics.ts`
 * can duplicate the same byte-for-byte value onto `client_ecs_config_state`
 * (additive in the first migration PR; session keeps emitting it for the
 * parity-validation window before it is dropped in a follow-up PR).
 *
 * Mutates `defaultFeatureFlags` in place (sorts it) to match the existing
 * session behavior — subsequent calls observe the already-sorted array.
 */
export function getSerializedFeatures(defaultFeatureFlags: string[] | undefined, accountFeatureFlags: string[] | undefined, isGlobalAccount: boolean): string {
    // the event is the same instance
    const flags = defaultFeatureFlags || [];
    // the sorted list will use less memory in kusto as it will aggregate better
    flags.sort();
    
    let featureFlags: string[];
    // For secondary accounts, only log the feature flags that differ from the primary account's defaults
    if (!isGlobalAccount && accountFeatureFlags) {
        featureFlags = getArrayDelta(accountFeatureFlags, flags).sort();
    }
    else {
        featureFlags = flags;
    }
    return JSON.stringify(featureFlags);
}

function getEnabledEcsFeatureNames(features: unknown): string[] | undefined {
    if (!features) {
        return undefined;
    }
    
    try {
        if (typeof features !== "object" || Array.isArray(features)) {
            trace.warn("Unexpected ECS features payload shape in session telemetry.", "analytics");
            return undefined;
        }
        
        const enabledEcsFeatures = Object.entries(features as Record<string, unknown>)
            .filter(([, value]) => value === true)
            .map(([featureName]) => featureName)
            .sort();
        
        return enabledEcsFeatures.length > 0 ? enabledEcsFeatures : undefined;
    }
    catch {
        trace.warn("Failed to serialize ECS features for session telemetry.", "analytics");
        return undefined;
    }
}

/**
 * Returns the serialized ECS feature names exactly the way session
 * stamps them on the `ECSFeatures` column.
 *
 * Maintains a module-level cache (`cachedGlobalEcsFeatures`) populated by
 * the first primary-account call, then used to delta-encode secondary
 * account emissions. Exported so the EcsConfigStateStamping emission
 * sites can mirror session's exact value during the parity-validation
 * window before the column is dropped from `session`.
 */
export function getSerializedEcsFeatures(features: unknown, isGlobalAccount: boolean): string | undefined {
    const ecsFeatureNames = getEnabledEcsFeatureNames(features);
    if (!ecsFeatureNames) {
        return undefined;
    }
    
    if (isGlobalAccount) {
        cachedGlobalEcsFeatures = new Set(ecsFeatureNames);
        return JSON.stringify(ecsFeatureNames);
    }
    
    if (cachedGlobalEcsFeatures) {
        const delta = getArrayDelta(ecsFeatureNames, cachedGlobalEcsFeatures);
        return delta.length > 0 ? JSON.stringify(delta) : undefined;
    }
    
    return JSON.stringify(ecsFeatureNames);
}
